// Utilities for extracting and normalizing teaser data for DS mapping

import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"
import { SPACER_ALIAS, VALID_SPACER_SIZES } from "@/constants/index"
import {
  HTML_STRING_REGEX_OPT1,
  HTML_STRING_REGEX_OPT2,
  SANITIZE_TAG_OR_UNDEFINED_REGEX,
} from "@/constants/regex"
import { sanitizeInnerHtml } from "@/utils/helper"
import React from "react"

/**
 * Strips HTML tags from a string.
 *
 * @param {string} html - The HTML string to strip.
 * @return {string} The plain text with HTML tags removed.
 */
export function stripHtml(html) {
  if (!html) return ""
  return html.replace(/<[^>]+>/g, "").trim()
}

/**
 * Determines if an item is a teaser.
 *
 * Supports both `slingResourceType` and `:type` properties.
 *
 * @param {Object} item - The item to check.
 * @return {boolean} True if the item is a teaser, false otherwise.
 */
export function isTeaser(item) {
  // Ensure a boolean is always returned
  return !!(
    item?.slingResourceType === "teaser" || item?.[":type"]?.includes("teaser")
  )
}

/**
 * Extracts and normalizes teaser data for Design System consumption.
 *
 * @param {Object} teaser - The teaser object to extract data from.
 * @param {string} [teaser.mainTitle] - The main title of the teaser.
 * @param {string} [teaser.title] - The title of the teaser.
 * @param {string} [teaser.description] - The description of the teaser.
 * @param {string} [teaser.fileReference] - The image file reference.
 * @param {Object} [teaser.imageResource] - The image resource object.
 * @param {string} [teaser.imageResource.fileReference] - The image file reference from imageResource.
 * @param {string} [teaser.imageResource.alt] - The alt text for the image.
 * @param {Array} [teaser.actions] - The array of CTA actions.
 * @param {string} [teaser.linkCTA] - The CTA link.
 * @param {string} [teaser.linkURL] - The fallback link URL.
 * @return {Object} The normalized teaser data:
 *
 */
export function extractTeaserData(teaser) {
  const imageUrl =
    teaser.fileReference || teaser.imageResource?.fileReference || ""
  const imageserverurl = teaser?.imageResource?.imageServerUrl || ""
  const imageID = teaser?.imageResource?.assetID || ""

  // Get CTA link
  let linkUrl = ""
  if (Array.isArray(teaser.actions) && teaser.actions.length > 0) {
    const action = teaser.actions.find(a => a.url || a.link)
    if (action) linkUrl = action.url || action.link
  }
  if (!linkUrl) linkUrl = teaser.linkCTA || teaser.linkURL || ""

  return {
    title: stripHtml(teaser.title || teaser.mainTitle || ""),
    description: stripHtml(teaser.description || ""),
    imageUrl,
    linkUrl,
    alt: teaser.imageResource?.alt || "",
    ctaLabel: teaser.actions?.[0]?.title || teaser.actions?.[0]?.text || "",
    tryimageUrl: `${imageserverurl}${imageID}`,
  }
}

/**
 * Maps an array of card data to the required card object structure for rendering.
 *
 * @param {Array<Object>} cards - The array of card data objects (from extractTeaserData).
 * @param {string} [pageType=""] - The page type string, used for tracking attributes in analytics.
 * @return {Array<Object>} The mapped array of card objects, each containing image, CTA, title, and description.
 */
export function mapCards(cards, pageType = "") {
  return cards.slice(0, 2).map(card => {
    const base = {
      image: {
        src: `${card.tryimageUrl}`,
        alt: card?.alt,
        "data-mode": "smartcrop",
      },
      CTA: {
        text: sanitizeInnerHtml(card?.ctaLabel),
        href: modifyHref(card?.linkUrl),
        trackingAttributes: {
          "data-datalayer-id": "kds-element-navigation",
          "data-datalayer-data": JSON.stringify({
            clickInternalLinks: "true",
            eventAction: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}:${sanitizeInnerHtml(
              card?.ctaLabel
            )?.toLowerCase()}`,
            eventName: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}:${sanitizeInnerHtml(
              card?.ctaLabel
            )?.toLowerCase()}`,
            eventType: "navigation",
            internalLinkName: sanitizeInnerHtml(card?.ctaLabel)?.toLowerCase(),
            internalLinkPosition: `${pageType}:teaser`,
            internalLinkType: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}`,
            internalLinkURL:
              card?.linkUrl && typeof window !== "undefined"
                ? `${window.location.origin}${card?.linkUrl
                    ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                    .join("")
                    .replace(".html", "")}`
                : "n/a",
            internalLinkZoneName: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}`,
            eventMsg: "n/a",
            eventStatus: "n/a",
          }),
        },
      },
      title: {
        text: sanitizeInnerHtml(card?.title),
        component: "h3",
      },
      description: {
        text: sanitizeInnerHtml(card?.description),
      },
    }
    return base
  })
}

/**
 * Returns a valid HTML/custom element tag or undefined.
 *
 * @param {string} tag - The tag name to validate.
 * @return {string|undefined} The validated tag name, or undefined if invalid.
 */
export function sanitizeTagOrUndefined(tag) {
  if (typeof tag !== "string" || !tag.trim()) return undefined
  const t = tag.trim()
  return SANITIZE_TAG_OR_UNDEFINED_REGEX.test(t) ? t : undefined
}

/**
 * Normalizes spacer sizes to the DS contract.
 *
 * @param {string} raw - The raw spacer value (alias or valid size).
 * @return {Object|undefined} An object with `{ size }` if valid, otherwise undefined.
 */
export function normalizeSpacer(raw) {
  if (!raw) return undefined
  const key = String(raw).toLowerCase().trim()
  const mapped = SPACER_ALIAS[key] || key
  const size = VALID_SPACER_SIZES.has(mapped) ? mapped : "none"
  return { size }
}

/**
 * Title mapper that preserves component if valid, and drops if text is a React node.
 *
 * @param {Object} heading - The heading object.
 * @param {string|Object} heading.text - The heading text or React element.
 * @param {string} [heading.component] - The HTML tag for the heading.
 * @return {Object|undefined} Mapped heading object or undefined.
 */
export function mapTitle(heading) {
  if (!heading) return undefined
  const text = isObj(heading) ? heading.text : heading
  if (!text) return undefined
  const out = { ...(isObj(heading) ? heading : {}), text }
  if (typeof text !== "object") {
    const comp = sanitizeTagOrUndefined(heading.component)
    if (comp) out.component = comp
    else delete out.component
  } else {
    delete out.component
  }
  return Object.keys(out).length ? out : undefined
}

/**
 * Detects if a string looks like HTML.
 *
 * @param {string} v - The string to check.
 * @return {boolean} True if the string contains HTML, false otherwise.
 */
export function isHtmlString(v) {
  return (
    typeof v === "string" &&
    (HTML_STRING_REGEX_OPT1.test(v) || HTML_STRING_REGEX_OPT2.test(v))
  )
}

/**
 * Text mapper for handling HTML strings, plain text, and React nodes.
 *
 * @param {Object} copy - The copy object.
 * @param {string|Object} copy.text - The text, HTML string, or React element.
 * @param {string} [copy.component] - The HTML tag for non-element text.
 * @return {Object|undefined} Mapped text object or undefined.
 */
export function mapText(copy) {
  if (!copy) return undefined
  const raw = isObj(copy) ? copy?.text : copy
  if (!raw) return undefined

  const text = isHtmlString(raw)
    ? React.createElement("div", {
        className: "kohler-kds-style-inherit",
        dangerouslySetInnerHTML: { __html: raw },
      })
    : raw

  const isElement = React.isValidElement(text)
  const comp =
    !isElement && typeof copy.component === "string" && copy.component.trim()
      ? copy.component.trim()
      : undefined

  const out = { text }
  if (comp) out.component = comp
  return out
}

/**
 * Image mapper that defaults missing alt to '' and returns undefined without src.
 *
 * @param {Object} img - The image object.
 * @param {string} img.src - The image source URL.
 * @param {string} [img.alt] - Optional alt text.
 * @return {Object|undefined} Mapped image object with guaranteed `alt`, or undefined if no src provided.
 */
export function mapImage(img) {
  return img?.src ? { ...img, alt: img?.alt ?? "" } : undefined
}

export const mapLink = lnk => {
  if (!lnk) return undefined

  return lnk
}

export const mapVideo = video => {
  if (!video || !video?.src || video?.src?.length === 0) return undefined

  if (video.videoMobileLandscape || video.videoMobilePortrait) {
    const defaultVideoUrl = video.src
    video.sources = []

    if (video.videoMobileLandscape) {
      video.sources.push({
        src: video.videoMobileLandscape,
        media: "(max-height: 640px) and (orientation: landscape)",
      })

      delete video.videoMobileLandscape
    }

    if (video.videoMobilePortrait) {
      video.sources.push({
        src: video.videoMobilePortrait,
        media: "(max-width: 640px) and (orientation: portrait)",
      })

      delete video.videoMobilePortrait
    }

    video.sources.push({
      src: defaultVideoUrl,
    })
  }

  return video
}

export const isObj = v => !!v && typeof v === "object" && !Array.isArray(v)

export const findByType = (node, includes) => {
  const test = n =>
    typeof n?.[":type"] === "string" &&
    n[":type"].toLowerCase().includes(includes.toLowerCase())
  if (test(node)) return node

  if (isObj(node?.[":items"])) {
    for (const k of Object.keys(node[":items"])) {
      const found = findByType(node[":items"][k], includes)
      if (found) return found
    }
  }
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findByType(child, includes)
      if (found) return found
    }
  }
  if (isObj(node)) {
    for (const k of Object.keys(node)) {
      if (k === ":items") continue
      const v = node[k]
      if (isObj(v) || Array.isArray(v)) {
        const found = findByType(v, includes)
        if (found) return found
      }
    }
  }
  return undefined
}

export const asArray = v => (Array.isArray(v) ? v : v != null ? [v] : [])

export const pickStr = v =>
  typeof v === "string" && v.trim() !== "" ? v : undefined

export const mapOverlayDirection = raw => {
  const v = String(raw ?? "")
    .toLowerCase()
    .replace(/\s+/g, "-")
  const allowed = new Set([
    "bottom-top",
    "top-bottom",
    "left-right",
    "right-left",
    "tint",
  ])
  return allowed.has(v) ? v : undefined
}

export const mapOverlayOpacity = raw => {
  const n = Number(raw)
  const allowed = new Set([10, 15, 20, 25, 30, 35, 40, 45])
  return allowed.has(n) ? n : undefined
}
